import { useEffect, useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ReferenceArea, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "@/lib/api";

const BAND_COLORS = ["#FF6B00", "#4D00FF", "#00A86B", "#FF3366", "#0A0A0A"];

const toBands = (series) => {
  const bands = [];
  series.forEach((d) => {
    const last = bands[bands.length - 1];
    if (last && last.name === d.campaign) last.x2 = d.date;
    else if (d.campaign) bands.push({ name: d.campaign, x1: d.date, x2: d.date });
  });
  return bands;
};

const CampaignTable = ({ rows, colorOf }) => (
  <table className="w-full text-sm" data-testid="click-trend-campaigns">
    <thead className="font-mono uppercase text-[10px] tracking-widest pcs-bg-orange">
      <tr>{["Campaign", "Clicks", "Active days", "Clicks / day", "Look share"].map((h) => <th key={h} className="text-left p-2 border-b-2 border-black">{h}</th>)}</tr>
    </thead>
    <tbody>
      {rows.map((c, i) => (
        <tr key={c.name} className="border-b border-black/20" data-testid={`click-trend-campaign-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>
          <td className="p-2 font-bold inline-flex items-center gap-2">
            <span className="w-2.5 h-2.5 border border-black" style={{ backgroundColor: colorOf(c.name) }} />
            {c.name}{i === 0 && <span className="font-mono text-[9px] uppercase tracking-widest pcs-bg-pink text-white px-1">Top</span>}
          </td>
          <td className="p-2 font-display text-lg">{c.clicks}</td>
          <td className="p-2 font-mono">{c.active_days}</td>
          <td className="p-2 font-mono">{c.per_day}</td>
          <td className="p-2 font-mono">{c.look_share}%</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export default function ClickTrendChart({ days }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setData(null);
    setError(false);
    api.get("/admin/clicks/daily", { params: { days } }).then((r) => setData(r.data)).catch(() => setError(true));
  }, [days]);

  const bands = useMemo(() => (data ? toBands(data.series) : []), [data]);
  const names = useMemo(() => [...new Set(bands.map((b) => b.name))], [bands]);
  const colorOf = (n) => BAND_COLORS[names.indexOf(n) % BAND_COLORS.length] || "#BDBDBD";

  if (error) return <div className="text-sm pcs-text-pink" data-testid="click-trend-error">Could not load the click chart. Try another range or reload.</div>;
  if (!data) return <div className="font-mono uppercase text-xs tracking-widest opacity-60">Loading chart...</div>;

  return (
    <div className="space-y-4" data-testid="click-trend">
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="font-display text-xl tracking-tight">Daily clicks</h3>
        <div className="flex gap-3 flex-wrap font-mono uppercase text-[10px] tracking-widest">
          {names.map((n) => (
            <span key={n} className="inline-flex items-center gap-1"><span className="w-3 h-3 opacity-30" style={{ backgroundColor: colorOf(n) }} />{n}</span>
          ))}
        </div>
      </div>
      <div className="h-72 border-2 border-black bg-white p-2" data-testid="click-trend-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data.series} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
            <CartesianGrid stroke="#0A0A0A" strokeOpacity={0.08} vertical={false} />
            {bands.map((b) => (
              <ReferenceArea key={`${b.name}-${b.x1}`} x1={b.x1} x2={b.x2} fill={colorOf(b.name)} fillOpacity={0.12} ifOverflow="extendDomain" />
            ))}
            <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 10, fontFamily: "Space Mono" }} minTickGap={16} />
            <YAxis allowDecimals={false} domain={[0, (max) => Math.max(max, 4)]} tick={{ fontSize: 10, fontFamily: "Space Mono" }} />
            <Tooltip
              contentStyle={{ border: "2px solid #0A0A0A", borderRadius: 0, fontFamily: "Space Mono", fontSize: 11 }}
              labelFormatter={(d, p) => `${d}${p?.[0]?.payload?.campaign ? ` · ${p[0].payload.campaign}` : ""}`}
            />
            <Legend iconType="square" wrapperStyle={{ fontFamily: "Space Mono", fontSize: 10, textTransform: "uppercase" }} />
            <Bar dataKey="main" name="Main button" stackId="a" fill="#FF3366" />
            <Bar dataKey="look" name="Complete the look" stackId="a" fill="#4D00FF" />
            <Bar dataKey="season" name="Season pages" stackId="a" fill="#00A86B" />
            <Bar dataKey="other" name="Other" stackId="a" fill="#BDBDBD" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {data.campaigns.length > 0 ? (
        <div className="overflow-x-auto"><CampaignTable rows={data.campaigns} colorOf={colorOf} /></div>
      ) : (
        <div className="text-sm opacity-70" data-testid="click-trend-campaigns-empty">No campaign clicks in this period.</div>
      )}
    </div>
  );
}
